import type { Employment } from "@credit-acceptance/step6-bff-client";

import { PHONE_MASK_PLACEHOLDER, stripMaskPlaceholder } from "../constants";
import {
  type CallScriptEntry,
  type CallScriptVerifiedFields,
  EMPTY_CALL_SCRIPT_VERIFIED_FIELDS,
} from "./callScript.types";

// Maps the BFF's incomeFrequencyType enum to the app's PAY_FREQUENCY_OPTIONS values.
const INCOME_FREQUENCY_TO_PAY_FREQUENCY: Record<string, string> = {
  WEEKLY: "Weekly",
  BI_WEEKLY: "Bi-Weekly",
  TWICE_MONTHLY: "2x-Monthly",
  MONTHLY: "Monthly",
  ANNUAL: "Annual",
};

// Same mapping keyed on the BFF's numeric incomeFrequencyTypeId (1-5) instead of the
// string enum. The BFF's `incomeFrequencyType` field is never actually populated by
// CAS (CAS only ever sends `incomeFrequencyTypeId`), so this is the fallback that
// actually fires in practice, not the string branch above — same phantom-field gap
// already fixed for the Income Calculator's mapEmploymentToIncome (incomeMapping.ts).
const INCOME_FREQUENCY_ID_TO_PAY_FREQUENCY: Record<number, string> = {
  1: "Weekly",
  2: "Bi-Weekly",
  3: "2x-Monthly",
  4: "Monthly",
  5: "Annual",
};

/**
 * Fields with no BFF counterpart — the "Saved Information" column always
 * renders these as an em dash. `phoneNumberDialed` is excluded: its "saved"
 * value comes from the latest Call Script history entry for this employment
 * slot (see {@link buildSavedFieldsForAnswers} and the modal's `savedFields`
 * construction), not from the Employment record.
 */
export const NO_SAVED_COUNTERPART_FIELDS = new Set<keyof CallScriptVerifiedFields>([
  "verificationOutsourced",
  "originalNumberBad",
  "verifiedWith",
  "dateOfTermination",
  "avgHoursPerWeek",
]);

/**
 * Maps a saved Employment record into the modal's field shape, for rendering
 * the read-only "Saved Information" controls (same input types as "Verified
 * Information", disabled). Fields in {@link NO_SAVED_COUNTERPART_FIELDS} are
 * left at their empty defaults and rendered as an em dash instead.
 * @param {Employment | undefined} employment - The applicant's saved employment record, if any.
 * @returns {CallScriptVerifiedFields} The saved-fields state.
 */
export function mapEmploymentToSavedFields(employment: Employment | undefined): CallScriptVerifiedFields {
  if (!employment) {
    return { ...EMPTY_CALL_SCRIPT_VERIFIED_FIELDS };
  }
  const workLocation = employment.workLocations?.[0];
  return {
    ...EMPTY_CALL_SCRIPT_VERIFIED_FIELDS,
    employerName: employment.employerName ?? "",
    hireDate: employment.hireDate ?? "",
    employmentType: employment.employmentTypeId === undefined ? "" : String(employment.employmentTypeId),
    occupation: employment.occupation ?? "",
    streetNumber: employment.streetNumber ?? "",
    streetName: employment.streetName ?? "",
    apartmentNumber: employment.apartmentNumber ?? "",
    city: employment.city ?? "",
    state: employment.state ?? "",
    zip: employment.zip ?? "",
    employerPhone: stripMaskPlaceholder(employment.employerPhone ?? "", PHONE_MASK_PLACEHOLDER),
    differentWorkLocation: Boolean(employment.workLocations && employment.workLocations.length > 0),
    workLocationName: workLocation?.locationName ?? "",
    workLocationStreetNumber: workLocation?.streetNumber ?? "",
    workLocationStreetName: workLocation?.streetName ?? "",
    workLocationApartmentNumber: workLocation?.apartmentNumber ?? "",
    workLocationCity: workLocation?.city ?? "",
    workLocationState: workLocation?.state ?? "",
    workLocationZip: workLocation?.zip ?? "",
    workLocationPhone: stripMaskPlaceholder(workLocation?.phone ?? "", PHONE_MASK_PLACEHOLDER),
    payFrequency: employment.incomeFrequencyType
      ? (INCOME_FREQUENCY_TO_PAY_FREQUENCY[employment.incomeFrequencyType] ?? "")
      : employment.incomeFrequencyTypeId
        ? (INCOME_FREQUENCY_ID_TO_PAY_FREQUENCY[employment.incomeFrequencyTypeId] ?? "")
        : "",
    grossPayPerPeriod: employment.dscEnteredMonthlyIncomePerPayPeriod ?? employment.incomePerPayPeriodAmount ?? "",
  };
}

/**
 * Builds the baseline used to detect "match" vs. "mismatch" on save: the
 * employment-backed fields, overlaid with the latest saved Call Script
 * entry's values for the {@link NO_SAVED_COUNTERPART_FIELDS} (the fields with
 * no Employment/WorkLocation backing — their only prior "saved" value is
 * whatever was submitted last time).
 * @param {Employment | undefined} employment - The applicant's saved employment record, if any.
 * @param {CallScriptEntry | undefined} latestEntry - The most recent Call Script history entry, if any.
 * @returns {CallScriptVerifiedFields} The saved-fields baseline used to build verification answers.
 */
export function buildSavedFieldsForAnswers(
  employment: Employment | undefined,
  latestEntry: CallScriptEntry | undefined
): CallScriptVerifiedFields {
  const saved = mapEmploymentToSavedFields(employment);
  const priorAnswers = latestEntry?.verifiedFields;
  if (!priorAnswers) {
    return saved;
  }
  return {
    ...saved,
    phoneNumberDialed: priorAnswers.phoneNumberDialed,
    verificationOutsourced: priorAnswers.verificationOutsourced,
    originalNumberBad: priorAnswers.originalNumberBad,
    verifiedWith: priorAnswers.verifiedWith,
    dateOfTermination: priorAnswers.dateOfTermination,
    avgHoursPerWeek: priorAnswers.avgHoursPerWeek,
  };
}
